// locales/en.ts — English
import type { Translations } from './es';

export const en: Translations = {

  nav: {
    dashboard:  'Dashboard',
    newSession: '+ Session',
    import:     '⬆ Import',
    stats:      '◈ Stats',
    sessions:   'History',
    calendar:   'Calendar',
    goals:      'Goals',
    staking:    'Staking',
    guide:      'Guide',
    dailyQuiz:  'Daily Quiz',
    blog:       'Blog',
  },

  header: {
    bankroll:    'BANKROLL',
    pnlTotal:    'P&L TOTAL',
    syncing:     'Syncing...',
    plan:        'Plan',
    logout:      'Log out',
    resetData:   'Reset',
    upgradePro:  '⬆ Pro',
    settings:    'Settings',
  },
    // ─── Settings modal ───────────────────────────────────────────────────────
  settings: {
    title: 'Settings',
    savingChanges: 'Saving…',
    privacy: {
      title: 'Privacy & Data Sharing',
      description: 'Control how your stats are used to improve GrindLog for everyone.',
      shareStatsLabel: 'Share my anonymous stats',
      shareStatsHelp: 'Helps GrindLog build population benchmarks. When we have enough data, your healthy ranges will be calculated from real winning players at your stake instead of theoretical solver estimates.',
      whatIsSharedSummary: 'What gets shared exactly?',
      sharedItem1: 'Aggregated stats per tournament (VPIP, PFR, CBet, etc.) — only counts and percentages, never raw hand histories.',
      sharedItem2: 'Buy-in range and tournament format, so we can group benchmarks by stake.',
      sharedItem3: 'An anonymous identifier — never your email, your name, or anything that links back to you.',
      sharedItem4: 'You can revoke this anytime and your future imports will stop being shared.',
      revokeConfirmMessage: 'Do you also want to delete your historical shared data? This cannot be undone.',
    }
  },
    // ─── Announcements modal ──────────────────────────────────────────────────
  announcements: {
    title: "What's New",
    cta:   'Got it',
  },

    // ─── Opt-in modal (post-first-import) ─────────────────────────────────────
  optInModal: {
    title: 'Help build better benchmarks',
    intro: 'GrindLog uses theoretical solver ranges to flag your leaks. They work, but they assume opponents play GTO — which is rarely true at micro stakes. We can do better with your help.',
    bullet1: 'Share your anonymous stats and we\'ll calculate healthy ranges from REAL winning players at your stake.',
    bullet2: 'Only counts and percentages are shared. Never your hand histories, your hole cards, or your identity.',
    bullet3: 'You\'ll see population benchmarks once we have enough data, segmented by stake and format.',
    bullet4: 'You can change this anytime in Settings.',
    fineprint: 'By accepting, you allow GrindLog to include your stats in anonymous aggregates. We never share individual data with third parties.',
    acceptBtn: 'Yes, share my stats',
    savingBtn: 'Saving…',
    acceptedBtn: '✓ Thanks!',
    declineBtn: 'Not now',
    changeLater: 'You can change this anytime from Settings.'
  },

  auth: {
    email:           'Email',
    password:        'Password',
    login:           'Log in',
    register:        'Sign up',
    registerFree:    'Sign up for free',
    loginTitle:      'Welcome back',
    registerTitle:   'Create account',
    resetTitle:      'Reset password',
    loginSub:        'Your sessions are waiting.',
    registerSub:     'Free. No card. No limits.',
    resetSub:        'We\'ll send a link to your email.',
    googleLogin:     'Continue with Google',
    magicLink:       'Magic link',
    sendLink:        'Send link',
    checkEmail:      'Check your email — we sent you a login link',
    confirmEmail:    'Check your email to confirm your account.',
    resetSent:       'We sent you a password reset link.',
    wrongCredentials:'Incorrect email or password',
    weakPassword:    'Password must be at least 6 characters',
    tabLogin:        'Log in',
    tabRegister:     'Sign up',
    tabMagic:        'Magic link',
  },

  dashboard: {
    bankroll:       'BANKROLL',
    netProfit:      'NET PROFIT',
    winRate:        'WIN RATE',
    totalRoi:       'TOTAL ROI',
    sessions:       'SESSIONS',
    bestSession:    'BEST SESSION',
    worstSession:   'WORST SESSION',
    totalInvested:  'TOTAL INVESTED',
    roiByFormat:    'ROI BY FORMAT',
    recentSessions: 'RECENT SESSIONS',
    viewAll:        'View all →',
    noSessions:     'No sessions yet',
    firstSession:   'Log your first session to start tracking',
  },

  session: {
    title:          'NEW SESSION',
    typeCash:       'CASH GAME',
    typeTournament: 'TOURNAMENT',
    typeMulti:      'MULTI-TOURNEY',
    date:           'Date',
    platform:       'Platform',
    duration:       'Duration (min)',
    notes:          'Notes',
    notesPlaceholder:'General notes...',
    stakes:         'Stakes (e.g. NL10, 0.05/0.10)',
    stakesPlaceholder:'NL10',
    buyIn:          'Buy-In ($)',
    cashOut:        'Cash Out ($)',
    grossProfit:    'Gross profit',
    staked:         'Staked session',
    stakerName:     'Backer name',
    myPct:          'My % of profit',
    save:           'Save Session',
    saved:          '✓ Saved',
    tournamentName: 'Tournament name (optional)',
    tournamentBuyIn:'Buy-In $',
    rebuys:         'Rebuys',
    addOn:          'Add-On $',
    prize:          'Prize $',
    bounties:       'Bounties $',
    position:       'Position',
    entrants:       'Entrants',
    addTournament:  '+ Tournament',
    format:         'Format',
    levelStakes:    'Casino, tournament, level...',
    goal:           'GOAL',
  },

  sessionsTable: {
    title:        'HISTORY',
    date:         'Date',
    type:         'Type',
    platform:     'Platform',
    buyIn:        'Buy-In',
    cashOut:      'Cash Out',
    profit:       'Profit',
    duration:     'Duration',
    notes:        'Notes',
    cash:         'Cash',
    tournament:   'Tournament',
    multi:        'Multi',
    export:       'Export CSV',
    noResults:    'No sessions match those filters.',
    deleteConfirm:'Delete?',
    staking:      'Staking',
  },

  goals: {
    title:          'GOALS',
    completed:      'COMPLETED',
    createGoal:     'Create Goal',
    cancel:         'Cancel',
    type:           'Type',
    typeBankroll:   'Bankroll target',
    typeSessions:   'Sessions to play',
    typeRoi:        'ROI target %',
    description:    'Description (optional)',
    deadline:       'Deadline (optional)',
    targetBankroll: 'Target $',
    targetSessions: '# sessions',
    targetRoi:      'ROI %',
    empty:          'No active goals. Create one to stay focused!',
    labelBankroll:  'Reach ${{val}} bankroll',  // eslint-disable-line no-template-curly-in-string
    labelSessions:  'Play {{val}} sessions',
    labelRoi:       'Maintain {{val}}% ROI',
  },

  staking: {
    title:          'STAKING',
    byBacker:       'BY BACKER',
    sessions:       'Sessions',
    stakedSessions: 'Staked sessions',
    grossProfit:    'Total gross profit',
    backerShare:    'Backer share',
    myProfit:       'My net profit',
    gross:          'Gross',
    myPart:         'My part',
    theirPart:      'Their part',
    noStaked:       'No staked sessions recorded.',
    unnamed:        'Unnamed',
  },

  import: {
    title:          'IMPORT POKERCRAFT',
    subtitle:       'Drag .txt files exported from GGPoker → PokerCraft → History',
    dropzone:       'Drag PokerCraft .txt files here',
    dropzoneHint:   'You can drop a full week\'s folder at once',
    processing:     'Processing files...',
    viewStats:      '◈ View Stats & AI ({{n}} tournaments) →',
    date:           'Date',
    tournament:     'Tournament',
    format:         'Format',
    buyIn:          'Buy-In',
    hands:          'Hands',
    busts:          'Busts',
    detected:       'Detected',
    buyInTotal:     'Total buy-in',
    pos:            'Pos.',
    prize:          'Prize $',
    bounties:       'Bounties $',
    bust:           'Bust',
    busted:         'Busted',
    survived:       'Survived',
    selectAll:      'Sel. all',
    importBtn:        'Import {{n}} tournaments →',
    importAndViewBtn: 'Save & view stats ({{n}}) →',
    importSaving:     'Saving...',
    importDone:     '✓ {{n}} tournaments imported successfully',
    howToTitle:     'HOW TO EXPORT FROM GGPOKER',
    howToStep1:     'Open GGPoker → PokerCraft',
    howToStep2:     'Go to History → choose date range',
    howToStep3:     'Click Export → downloads a folder with .txt files',
    howToStep4:     'Drag all .txt files here',
  },

  stats: {
    title:        'PLAYING STATISTICS',
    subtitle:     '{{hands}} hands · {{tournaments}} tournaments',
    empty:        'Import tournaments from PokerCraft to see your statistics.',
    tabPreflop:   'Preflop',
    tabFlop:      'Flop',
    tabTurn:      'Turn',
    tabRiver:     'River',
    tabShowdown:  'Showdown',
    flop:         'FLOP',
    turn:         'TURN',
    river:        'RIVER',
    vsCbetFlop:   'VS CBET FLOP',
    vsCbetTurn:   'VS DOUBLE BARREL',
    vsCbetRiver:  'VS CBET RIVER',
    donkBets:     'DONK BETS',
    probeBets:    'PROBE BETS',
    checkRaise:   'CHECK-RAISE',
    positions:    'POSITIONS',
    fold:         'Fold',
    call:         'Call',
    aiTitle:      '◈ AI ANALYSIS',
    aiSub:        'Claude analyzes your leaks and generates an EV-prioritized diagnosis',
    aiGenerate:   'Generate analysis →',
    aiLoading:    'Analyzing...',
    aiAnalyzing:  'Analyzing {{n}} hands...',
    aiRegenerate: '↺ Regenerate',
    aiNeedHands:  'You need {{n}}+ hands (you have {{have}})',
    aiConsent:    'Share my anonymous stats to calculate population benchmarks by stake',
    aiError:      'Connection error. Please try again.',
    aiDevError:   'Cannot connect to analysis server. In development: run node dev-server.js',
    paywallText:  '🔒 AI Coach is a Pro feature. Detect your leaks and improve your win rate.',
    paywallBtn:   'Upgrade to Pro — from $4/mo',
    // ─── Breakdown gate ──────────────────────────────────────────────────
    upgradePrompt: {
      text: 'Upgrade to Pro to unlock the {{feature}} breakdown.',
      btn:  'Upgrade to Pro — from $4/mo',
    },
    opt:          'opt',
    // ─── Generic ─────────────────────────────────────────────────────────
    insufficientSample: 'insufficient sample',

    // ─── Fase 2: Fold to 3Bet split ──────────────────────────────────────
    foldTo3betVsAllin: 'vs All-in / Shove',
    foldTo3betVsNonAllin: 'vs Non-Allin 3Bet',
        // ─── Fase 4: Matchup link inside Fold to 3Bet ────────────────────────
    foldTo3betShowMatchup: '+ View matchup breakdown →',
    foldTo3betHideMatchup: '− Hide matchup breakdown',

    // ─── Fase 3: Open Raise (RFI) ────────────────────────────────────────
    openRaise: 'Open Raise (RFI)',
    squeeze:   'Squeeze',
    fourbet:   '4Bet',
    openLimp:  'Open Limp',

    // ─── Fase 1: CBet Flop matchup breakdown ─────────────────────────────
    matchup: {
      empty: 'No matchup data yet. Play more hands to see breakdown by position.',
      warning: 'No matchup has enough sample yet (minimum {{n}} opportunities). The overall number above is an average across different scenarios that likely play very differently. Keep playing to unlock per-matchup breakdown.',
      cbetFlopTitle: 'CBet Flop by hero × villain position',
      threebetTitle: '3Bet by hero × opener position',
      foldTo3betTitle: 'Fold to 3Bet by hero × 3bettor position',
      multi: 'Multiway (3+ players)',
      thMatchup: 'Matchup',
      thCbet: 'CBet %',
      thThreebet: '3Bet %',
      thFold: 'Fold %',
      thBenchmark: 'Healthy',
      thSample: 'Sample',
      thSuccess: 'Success %',
      moreInsufficient: "{{n}} more matchup(s) with insufficient sample",
      benchmarkDisclaimer: 'Healthy ranges are approximate solver/coaching consensus. They vary significantly with SPR, board texture and meta of your stake. Use them as direction, not as ground truth.'
    },
    // ─── VPIP by position breakdown ──────────────────────────────────────
    vpip: {
      rowLabel: 'VPIP by position',
      empty: 'No hands with known position yet. Import more hand histories to see your VPIP breakdown by position.',
      warning: 'No position has enough sample yet (minimum {{n}} hands per position). The overall VPIP above is an average across positions that play very differently. Keep playing to unlock per-position breakdown.',
      title: 'VPIP % by position',
      thPosition: 'Position',
      thVpip: 'VPIP %',
      thBenchmark: 'Healthy',
      thSample: 'Sample',
      lowTag: '(low)'
    },
    // ─── Fase 3: RFI by position breakdown ───────────────────────────────
    rfi: {
      empty: 'No open raise opportunities yet. Play more hands to see your RFI breakdown by position.',
      warning: 'No position has enough sample yet (minimum {{n}} opportunities per position). The overall number above is an average across positions that should play very differently. Keep playing to unlock per-position breakdown.',
      title: 'Open Raise % by position (RFI)',
      thPosition: 'Position',
      thRfi: 'RFI %',
      thLimp: 'Limp %',
      thBenchmark: 'Healthy',
      thSample: 'Sample',
      lowTag: '(low)'
    },
    foldToPush: {
      rowLabel: 'Fold to Push',
      title: 'Fold to Push by shover stack',
      empty: 'No fold-to-push spots yet. Import more hands to see how you respond to preflop all-ins.',
      warning: 'No stack bin has enough sample yet (minimum {{n}} spots). Keep playing to unlock evaluation.',
      thStack: 'Shover stack',
      thFold: 'Fold %',
      thBenchmark: 'Nash (fold %)',
      thSample: 'Sample',
      lowTag: '(low)',
    },
    pushFold: {
      label: 'Push/Fold (≤15bb)',
      title: 'Push/Fold by position — short stack only',
      empty: 'No push/fold opportunities yet. Play more short-stack hands to see your push frequency by position.',
      warning: 'No position has enough sample yet (minimum {{n}} opportunities). Keep playing short-stack to unlock evaluation.',
      thPosition: 'Position',
      thPush: 'Push %',
      thNash: 'Nash range',
      thLimp: 'Limp %',
      thMinRaise: 'Min-raise %',
      thSample: 'Sample',
      lowTag: '(low)',
      leakTooltip: 'Leak detected: limping or min-raising in push/fold territory',
      disclaimer: 'Nash ranges are chipEV approximations weighted to ~10bb. Tighten ranges in ICM-sensitive spots (bubble, final table). Limp and min-raise targets are 0% — any non-zero value is a fundamental leak in push/fold theory.',
      switchStackHint: "Push/Fold analysis is only available with the 'Short stack' filter. Switch the stack selector above to see your push/fold breakdown."
    },
    cbetTexture: {
      label: 'CBet Flop (by texture)',
      title: 'CBet Flop by board texture × position — heads-up only',
      empty: 'No HU cbet opportunities yet. Play more hands as preflop aggressor in heads-up pots to see your cbet frequency by board texture.',
      warning: 'No texture bucket has enough sample yet (minimum {{n}} opportunities). Keep playing to unlock evaluation.',
      thTexture: 'Texture',
      thPosition: 'Pos',
      thCbet: 'CBet %',
      thBenchmark: 'Healthy',
      thSample: 'Sample',
      lowTag: '(low)',
      texDry: 'Dry',
      texSemiWet: 'Semi-wet',
      texWet: 'Wet',
      texPaired: 'Paired',
      texMonotone: 'Monotone',
      disclaimer: 'Benchmarks are chipEV solver approximations for HU SRP pots. Actual optimal frequency varies with SPR, exact board, and villain type. Multiway cbet is tracked separately — this breakdown only covers heads-up flops.'
    },
    cbetTextureMulti: {
      label: 'CBet Flop (multiway)',
      title: 'CBet Flop by texture — 3+ players to the flop',
      empty: 'No multiway cbet opportunities yet. Play more hands as preflop aggressor in pots with 3+ players to see your multiway cbet frequency by texture.',
      warning: 'No texture bucket has enough sample yet (minimum {{n}} opportunities). Keep playing to unlock evaluation.',
      disclaimer: 'Multiway cbet benchmarks are substantially lower than heads-up because range advantage dilutes with each extra player. In multiway, cbet only when your HAND itself can support the bet (top pair+, strong draws) — not because of range equity. Sizings of 33%–50% are often preferred over 66%.'
    },
    cbet3BetPot: {
      rowLabel:    'CBet Flop (3bet pot)',
      title:       'CBet Flop in 3bet pots — IP vs OOP',
      empty:       'No 3bet pot cbet spots yet as the 3bettor. Play more hands to see this breakdown.',
      thPos:       'Pos',
      thCbet:      'CBet %',
      thBenchmark: 'Healthy',
      thSample:    'Sample',
      lowTag:      '(low)',
      disclaimer:  'GTO benchmarks for HU 3bet pots (SPR ~7-9). IP: polar ranges favor frequent large bets. OOP: more checks to protect range; cbets with nutted hands and selective bluffs.',
    },
    dateFilter: {
      last7:       'Last 7 days',
      last30:      'Last 30 days',
      thisMonth:   'This month',
      lastMonth:   'Last month',
      allTime:     'All time',
      from:        'From',
      to:          'To',
      noData:      'No tournaments in this period. Try a wider range.',
    },
  },

  calendar: {
    sessions:    'Sessions',
    profit:      'Profit',
    winDays:     'Winning days',
    lossDays:    'Losing days',
    next:        'Next ›',
    prev:        '‹ Prev',
    tournaments: '{{n}} tournaments',
  },

  upgrade: {
    title:          'GrindLog Pro',
    subtitle:       'AI coaching + population benchmarks',
    featureAi:      'AI Coach — leak diagnosis with artificial intelligence',
    featureBench:   'Benchmarks by stake — compare yourself vs the field',
    featureSync:    'Cloud sync — your data on any device',
    featureExport:  'Advanced export — CSV with all metrics',
    featureFuture:  'Everything coming next — Pro price locked in today',
    monthly:        'Monthly plan',
    yearly:         'Annual plan · USD 5/mo',
    yearlyBadge:    '28% OFF',
    launchBanner:   '🚀 Launch price · Only for early users',
    monthlyAmount:  'USD 4',
    monthlyPeriod:  '/mo',
    monthlyRegular: 'then USD 7/mo',
    monthlyLabel:   '3 months · then regular price',
    yearlyAmount:   'USD 60',
    yearlyPeriod:   '/yr',
    yearlyRegular:  'USD 5/mo · best value',
    yearlyLabel:    'Annual payment · no surprise renewals',
    cancel:         'Cancel anytime. Whop handles payment — we never store card data.',
    support:        'Issues?',
    errorCheckout:  'Error starting checkout',
    comingSoonTitle: 'Coming soon',
    comingSoonMessage: 'Pro version launching April 1st.',
    comingSoonSub: 'We\'re preparing the best analysis tools for your grind.',
    backSoon: 'Back to app',
  },

  common: {
    loading:  'Loading',
    saving:   'Saving...',
    delete:   'Delete',
    cancel:   'Cancel',
    confirm:  'Confirm?',
    yes:      'Yes',
    no:       'No',
    close:    'Close',
    currency: 'USD',
    free:     'FREE',
    pro:      'PRO',
  },

  guide: {
    title:          '📖 How to use GrindLog',
    subtitle:       'Import your GGPoker tournaments in under 2 minutes and start seeing your real stats.',
    tabGuide:       '📋 Step-by-step guide',
    tabStats:       '📊 Understanding your stats',
    tabFaq:         '❓ FAQ',
    zoomHint:       'Click to enlarge',

    introText:      'GrindLog imports directly from PokerCraft (the analysis tool built into GGPoker). No manual data entry — the process is fully automatic.',

    step1Title:     'Open PokerCraft from the GGPoker lobby',
    step1Body:      'In the GGPoker client, look at the bottom bar of the screen. Between the Responsible Gaming banner and the language selector you will find the PokerCraft icon. Click it to open.',
    step1Tip:       'PokerCraft is free and included with your GGPoker account.',
    step1ImgAlt:    'GGPoker lobby with PokerCraft button',

    step2Title:     'Go to the Tournaments tab and select a date range',
    step2Body:      'Inside PokerCraft, click the Tournaments tab. Use the date filters to select the period you want to analyze. Once tournaments appear, you will see two green download buttons in the top right:',
    step2Li1:       'Download Game Summaries — contains exact buy-in, prize, final position, and number of players',
    step2Li2:       'Download Game Histories — contains all your played hands (to calculate VPIP, PFR, CBet, etc.)',
    step2Tip:       'You can filter by tournament name or buy-in to export only what you need.',
    step2ImgAlt:    'Tournaments tab in PokerCraft',

    step3Title:     'Download both files',
    step3Body:      'Click both download buttons. Each generates a separate ZIP file that will appear in your Downloads folder.',
    step3Warning:   'If you only download one type, your data will be incomplete: without the Summary there is no exact buy-in or final position; without the History there are no hand statistics.',
    step3ImgAlt:    'Download buttons in PokerCraft',

    step4Title:     'Unzip both files and gather all .txt files',
    step4Body:      'In your Downloads folder you will see two compressed folders. Unzip both (right-click → Extract). Inside you will find .txt files — some are Game History files and others are Game Summary files. You can put them all in a single folder.',
    step4Tip:       'If you downloaded multiple date ranges, you can mix all the .txt files in one folder — GrindLog automatically matches them by tournament ID.',
    step4ImgAlt:    'Downloaded files in file explorer',

    step5Title:     'Select all .txt files and drag them into GrindLog',
    step5Body1:     'In GrindLog, go to the Import tab. Select all the .txt files (Ctrl+A or Cmd+A) and drag them onto the drop zone, or click the zone to browse for them.',
    step5Body2:     'GrindLog automatically detects each file type and matches History + Summary pairs by tournament ID. In seconds you will see a table with all your tournaments.',
    step5Tip:       'You can import hundreds of files at once. GrindLog processes them all together.',
    step5ImgAlt:    'GrindLog import drop zone',

    step6Title:     'Review the table and click Import',
    step6Body:      'The table shows each tournament with its match status:',
    step6Li1:       '✓ Green — Has History + Summary, complete data',
    step6Li2:       'H Yellow — History only, no final result',
    step6Li3:       'S Grey — Summary only, no hand statistics',
    step6Body2:     'You can edit the buy-in or prize of any tournament before importing. When ready, click Import tournaments.',
    step6ImgAlt:    'Tournament table ready to import',

    step7Title:     'Done! Explore your stats and AI analysis',
    step7Body:      'Once imported, go to Stats to see your VPIP, PFR, CBet, and all your indicators with optimal reference ranges. Pro users can also generate an AI analysis that identifies your most costly leaks and gives you a concrete plan for your next sessions.',
    step7Tip:       'AI analysis is available on the Pro plan and requires at least 100 hands to generate a reliable diagnosis.',
    step7ImgAlt:    'GrindLog stats view',

    statsIntro:     'A quick guide to what each stat means and what the optimal range is for a regular MTT player.',
    statsCatPreflop:'🃏 Preflop',
    statsCatCbet:   '🔥 C-Bet (Continuation Bet)',
    statsCatDef:    '🛡️ Defense',
    statsCatSD:     '🏆 Showdown',
    statsColStat:   'Stat',
    statsColRange:  'Optimal range',
    statsColDesc:   'What it measures',
    statsTip:       "Optimal ranges are for regular MTT players (micro to mid stakes). In turbo or hyper-turbo formats some values shift. GrindLog's AI analysis adjusts the diagnosis based on your specific sample.",

    vpipDesc:       '% of hands you voluntarily put money in the pot. Too high = playing junk. Too low = too tight.',
    pfrDesc:        '% of hands where you raise preflop. Should be close to VPIP to avoid being passive.',
    pfrVpipDesc:    'Preflop aggression ratio. Below 70% means you are calling too much instead of raising.',
    threebetDesc:   '% of times you re-raise when someone opened. Too low = predictable and exploitable.',
    fold3bDesc:     '% of times you fold to a 3-bet. Above 65% = opponents steal easily.',
    atsDesc:        'Attempt to Steal: % of times you raise from BTN/CO/SB when folded to. Key to accumulating chips.',
    cbetFDesc:      '% of times you bet the flop when you were the preflop aggressor.',
    cbetTDesc:      'Double barrel: continuing to bet the turn after having bet the flop. Should be more selective than the flop.',
    cbetRDesc:      'Triple barrel: betting the river after barreling both flop and turn. Too low = leaving money on the table.',
    cbetFSucDesc:   '% of your flop c-bets that win without going to further streets. Effectiveness reference.',
    foldCbetFDesc:  '% of times you fold to a c-bet. Above 50% = opponents exploit you with any two cards.',
    foldCbetTDesc:  '% of times you fold on the turn to a bet. Key against aggressive players.',
    probeDesc:      '% of times you bet the turn when the opponent checked (you were the preflop caller). Indicates in-position aggression.',
    xraiseFlopDesc: 'Check-Raise Flop: % of times you raise after checking and facing a bet. Split by PFA (you raised preflop) vs non-PFA.',
    wtsdDesc:       '% of hands (where you saw the flop) that reach showdown. Too high = calling too much; too low = folding good hands.',
    wsdDesc:        'Win at Showdown: % of showdowns you win. Below 50% is a serious alarm.',
    wwsfDesc:       'Won When Saw Flop: % of times you win the pot having seen the flop. Measures overall postflop aggression.',
    afDesc:         'Aggression Factor: ratio of bets+raises vs calls. Below 2 = very passive postflop.',

    faq1q:  'What is the difference between Game History and Game Summary?',
    faq1a:  'Game History contains all hands you played card by card — used to calculate VPIP, PFR, CBet and the rest of your playing stats. Game Summary contains the tournament result: exact buy-in, final position, number of players, and prize. You need both for complete data. You can upload just one if you only need partial results.',
    faq2q:  "I uploaded the files but some tournaments show 'H' or 'S' instead of '✓'",
    faq2a:  'That means the tournament only has one file type (either History or Summary only). For a complete match you need to have downloaded both ZIPs from PokerCraft and unzipped all files together before uploading. If you already have them, go back to Import, click Clear, and drag all .txt files from both folders together.',
    faq3q:  'Why does the buy-in shown not match what I paid?',
    faq3a:  'The buy-in in the Summary is the tournament base price (what GGPoker charged). If you entered through a satellite, that buy-in does not reflect your real cost — you can edit it manually in the import table before confirming. GrindLog lets you modify any field before saving.',
    faq4q:  'Can I import tournaments from platforms other than GGPoker?',
    faq4a:  'Currently GrindLog automatically imports the PokerCraft (GGPoker) format. For other platforms you can log tournaments manually via + Session. More parsers are coming soon.',
    faq5q:  'How many hands do I need for the AI analysis to be reliable?',
    faq5a:  'GrindLog requires a minimum of 100 hands to enable AI analysis — with fewer the stats have too much variance to diagnose real leaks. For a solid diagnosis, at least 500–1000 hands is recommended. If you play short sessions, import several tournaments together.',
    faq6q:  'The AI analysis came out in English but I have the app in Spanish',
    faq6a:  'This was a known bug that has already been fixed. If it keeps happening, try switching to English and back to Spanish before generating the analysis.',
    faq7q:  'Is my data saved in the cloud or only on my device?',
    faq7a:  'GrindLog saves your sessions locally in your browser and also in the cloud with your account. You can access from any device as long as you are logged in with the same account.',
    faq8q:  'What happens if I close the browser without importing?',
    faq8a:  'The files you dragged are not saved until you click Import tournaments. If you close the tab before confirming, you will need to upload the files again. Once imported, the data is permanently saved to your account.',
    faq9q:  'Can I delete imported tournaments if I made a mistake?',
    faq9a:  'Yes. From the History tab you can see all your imported sessions and delete any of them. Changes are reflected immediately in the Dashboard and Stats.',
    faq10q: 'What does the Pro badge mean and what does it include?',
    faq10a: 'The Pro plan unlocks: AI leak analysis, Advanced stats (Triple Barrel, Probe, WWSF, W@SD), Goals and objective tracking, Calendar view with daily P&L, and the Staking module.',
  },
  footer: {
    // UI labels
    tagline: 'Poker tournament analytics.\nParse your hands. Study your game.',
    colProduct: 'Product',
    colLegal: 'Legal',
    colSupport: 'Support',
    linkDashboard: 'Dashboard',
    linkPricing: 'Pricing',
    linkGuide: 'Guide',
    lastUpdated: 'Last updated',
    lastUpdatedDate: 'March 2026',
    close: 'Close',
    disclaimer: 'GrindLog is a data analytics tool. It does not host, facilitate, or participate in real-money gaming or gambling of any kind.',

    // Titles
    termsTitle: 'Terms of Service',
    privacyTitle: 'Privacy Policy',
    refundTitle: 'Refund Policy ↗',

    // ── Terms of Service ──────────────────────────────────────────────────────
    termsContent: `**1. Acceptance of Terms**

By accessing or using GrindLog ("Service"), you agree to be bound by these Terms of Service. If you do not agree, please do not use the Service.

**2. Description of Service**

GrindLog is a data analytics SaaS platform for poker tournament players. It allows users to upload hand history files exported from third-party platforms and visualize personal performance statistics (VPIP, PFR, CBet, etc.). No wagers, games, or real-money transactions take place on our platform.

**3. User Account**

You are responsible for maintaining the confidentiality of your account credentials. Notify us immediately at support@grindlog.app of any unauthorized use.

**4. Acceptable Use**

You may not use the Service to upload content that violates third-party intellectual property rights, or to attempt to reverse-engineer or scrape any part of the platform.

**5. Subscriptions & Billing**

Subscriptions are billed in advance on a monthly or annual basis. You may cancel at any time from your account settings. Cancellation takes effect at the end of the current billing period.

**6. Refund Policy**

See our separate Refund Policy available in this footer.

**7. Intellectual Property**

All content, design, and code on GrindLog is the exclusive property of Franco Scioli / GrindLog. Your hand history data always remains your own.

**8. Disclaimer of Warranties**

The Service is provided "as is." We do not guarantee uninterrupted availability or that statistical outputs are free of errors.

**9. Limitation of Liability**

To the maximum extent permitted by law, GrindLog shall not be liable for indirect, incidental, or consequential damages arising from your use of the Service.

**10. Governing Law**

These Terms are governed by the laws of Argentina. Disputes shall be submitted to the courts of Buenos Aires, Argentina.

**11. Contact**

support@grindlog.app`,

    // ── Privacy Policy ────────────────────────────────────────────────────────
    privacyContent: `**1. Information We Collect**

**Account data:** email address and password (hashed). **Hand history data:** files you voluntarily upload, processed to generate statistics and immediately discarded; we do not store raw files longer than necessary. **Usage data:** page visits, feature interactions, and error logs for product improvement. **Payment data:** processed entirely by Whop. We never store credit card numbers.

**2. How We Use Your Information**

To provide, maintain, and improve the Service; to send transactional emails (account confirmation, subscription receipts); to respond to support requests.

**3. Data Sharing**

We do not sell, rent, or share your personal data with third parties, except: **Service providers:** Supabase (database), Vercel (hosting), Whop (payments), Resend (transactional email), each bound by their own privacy policies. **Legal requirements:** if required by law or court order.

**4. Data Retention**

Account data is retained while your account is active. You may request deletion at any time by emailing support@grindlog.app.

**5. Cookies**

We use only essential session cookies required for authentication. We do not use advertising or third-party tracking cookies.

**6. Security**

All data is encrypted in transit (TLS) and at rest. Access to production databases is restricted.

**7. Your Rights**

You have the right to access, correct, or delete your personal data. Contact: support@grindlog.app

**8. Children**

The Service is not directed at children under 16. We do not knowingly collect data from minors.

**9. Changes**

We will notify users of material changes to this policy via email.

**10. Contact**

support@grindlog.app`,

    // ── Refund Policy ─────────────────────────────────────────────────────────
    refundContent: `**Our Commitment**

We want you to be completely satisfied with GrindLog. If you're not, we'll make it right.

**14-Day Money-Back Guarantee**

If you are not satisfied with your purchase for any reason, you may request a **full refund within 14 days** of your initial payment. No questions asked.

**How to Request a Refund**

Send an email to support@grindlog.app with the subject line "Refund Request" and include: the email address associated with your account and your order number (available in your receipt). We will process your refund within 5–7 business days. Refunds are issued to the original payment method.

**After the 14-Day Window**

Requests submitted more than 14 days after purchase will be reviewed on a case-by-case basis. Exceptional circumstances (extended service outage, billing errors) will always be honored.

**Renewals**

If you have been charged for a renewal and wish to cancel, contact us within 48 hours of the renewal charge for a full refund of that renewal.

**Cancellations**

Cancelling your subscription stops future charges but does not automatically generate a refund for the current period. Please contact us if you believe a refund is warranted.

**Contact**

support@grindlog.app`,
  },

  // ── Blog ─────────────────────────────────────────────────────────────────────
  blog: {
    title:          'Blog',
    subtitle:       'Strategy, product and poker in depth',
    empty:          'No published posts yet.',
    readMore:       'Read more',
    back:           '← Back to blog',
    draft:          'Draft',
    newPost:        '+ New post',
    editPost:       'Edit',
    deletePost:     'Delete',
    deleteConfirm:  'Delete this post? This action cannot be undone.',
    formTitleCreate:'New post',
    formTitleEdit:  'Edit post',
    formTitleEs:    'Title (ES)',
    formTitleEn:    'Title (EN)',
    formExcerptEs:  'Excerpt (ES)',
    formExcerptEn:  'Excerpt (EN)',
    formContentEs:  'Content (ES) — Markdown',
    formContentEn:  'Content (EN) — Markdown',
    formOgImage:    'OG image URL (og:image / twitter:image)',
    formPublished:  'Publish',
    formSave:       'Save',
    formCancel:     'Cancel',
    saving:         'Saving...',
    minRead:        'min read',
  },

  // ── Teams ────────────────────────────────────────────────────────────────────
  teams: {
    navLabel: 'Teams',
    loading:   'Loading team...',
    noTeam:    "You're not in any team yet.",
    noTeamSub: 'If you received an invitation, click the link in the email to join.',

    createTitle:       'Create a team',
    createName:        'Team name',
    createNamePlaceholder: 'e.g. Phoenix Stable',
    createSlug:        'Team URL (slug)',
    createSlugHelp:    'Letters, numbers, and hyphens only. Cannot be changed later.',
    createSeats:       'Number of seats',
    createSeatsHelp:   'How many players you can add to the team.',
    createBtn:         'Create team',
    creating:          'Creating...',
    createError:       'Failed to create team. Please try again.',
    createProRequired: 'To create a team you need the GrindLog Teams plan. From $25/mo for a team of 5.',
    createUpgradeBtn:  '⬆ See Teams plans',

    myTeamTitle:       'My Team',
    coachLabel:        'Coach',
    memberSince:       'Member since',
    statusActive:      'Active',
    statusInvited:     'Invited (pending)',
    statusLeft:        'You left the team',

    syncTitle:         'Team Sync',
    syncDescription:   'When active, your aggregated stats (VPIP, CBet, 3Bet, etc.) are sent to your coach after each analysis. Raw hand histories are never shared.',
    syncOn:            'Sync active',
    syncOff:           'Sync disabled',
    syncToggleOn:      'Enable sync',
    syncToggleOff:     'Disable sync',
    syncToggleError:   'Could not change sync. Please try again.',

    payloadTitle:      'Last sync sent',
    payloadEmpty:      "You haven't synced any analysis yet.",
    payloadShowBtn:    'Show sent data',
    payloadHideBtn:    'Hide data',
    payloadDescription:'This is exactly what your coach receives when you sync.',

    leaveBtn:          'Leave team',
    leaveModalTitle:   'Leave the team?',
    leaveModalBody:    "Once you leave, your coach won't see your future stats. What should we do with your historical contributions?",
    leaveOptionAnon:   'Keep anonymously (coach sees team averages but not linked to you)',
    leaveOptionDelete: 'Delete everything (your stats are removed from the team)',
    leaveConfirmBtn:   'Confirm and leave',
    leaveCancelBtn:    'Cancel',
    leaving:           'Leaving...',
    leaveSuccess:      'You left the team.',
    leaveError:        'Failed to leave the team. Please try again.',

    dashboardTitle:    'Team Dashboard',
    seats:             '{{active}} / {{total}} seats',
    seatsLabel:        'Seats',
    rosterTitle:       'Roster',
    rosterEmpty:       'No members yet.',
    colPlayer:         'Player',
    colStatus:         'Status',
    colSync:           'Sync',
    colLastSync:       'Last sync',
    colHands:          'Hands',
    noSyncData:        'No data',
    syncOffNotice:     '{{count}} player(s) with sync off. Ask them to enable it to appear on the dashboard.',
    teamStatsTitle:    'Team Stats',
    teamStatsEmpty:    'No members have sync active yet.',
    teamStatsHands:    '{{count}} combined hands',

    inviteTitle:       'Invite players',
    inviteEmail:            "Player's email",
    inviteEmailPlaceholder: 'player@email.com',
    inviteNamePlaceholder:  'Name (optional)',
    inviteNameHelp:         'A name helps you identify the player in the roster.',
    inviteSendBtn:     'Send invitation',
    inviteSending:     'Sending...',
    inviteSuccess:        'Invitation sent to {{email}}.',
    inviteSuccessNoEmail: 'Invitation created for {{email}}, but the email failed to send. Share the invite link below manually.',
    inviteErrorSeats:  'Team is full ({{total}} seats used).',
    inviteErrorAlready:'This player is already a member.',
    inviteErrorGeneral:'Failed to send invitation. Please try again.',
    inviteLinkLabel:   'Invite link',
    inviteLinkCopy:    'Copy link',
    inviteLinkCopied:  'Copied!',

    removeMemberBtn:       'Remove',
    removeMemberConfirm:   'Remove {{email}} from the team? Their stats will no longer contribute to the dashboard.',
    removeMemberConfirmBtn:'Confirm',
    removeMemberError:     'Failed to remove member. Please try again.',

    settingsTitle:       'Team settings',
    settingsName:        'Team name',
    settingsSaveBtn:     'Save changes',
    settingsSaving:      'Saving...',
    settingsSaved:       'Saved!',
    publicDisplayLabel:  'Appear on GrindLog landing page',
    publicDisplayHelp:   "If enabled, your team name may appear in the 'Teams using GrindLog' section.",
    publicDisplayName:   "Team's public name",
    publicDisplayNameHelp:'The name shown publicly. Can differ from the internal name.',

    consentTitle:         'Invitation to {{teamName}}',
    consentSubtitle:      'Your coach wants to see your stats on the team dashboard.',
    consentSharedTitle:   'What is shared if you enable Team Sync?',
    consentShared1:       'Aggregated stats: VPIP, PFR, 3Bet, CBet by texture and position, Fold to Push, etc.',
    consentShared2:       'Number of hands analyzed and date range.',
    consentShared3:       'Stake range of the imported tournaments.',
    consentNotSharedTitle:'What is NEVER shared?',
    consentNotShared1:    'Your raw hand histories. They never leave your device.',
    consentNotShared2:    'Individual actions, opponents, cards, or exact per-hand timestamps.',
    consentNotShared3:    'Your email or any identifying information with third parties.',
    consentWhen:          'When is it sent?',
    consentWhenBody:      'Only when you import an analysis and have Team Sync active. Never in the background.',
    consentWho:           'Who can see it?',
    consentWhoBody:       'Only your team coach, {{coachEmail}}.',
    consentControl:       'Can you change your mind?',
    consentControlBody:   'Yes. You can disable Team Sync or leave the team at any time from the "My Team" section.',
    consentAcceptSync:    'Accept and join with Sync active',
    consentAcceptNoSync:  'Join without sharing data (Sync off)',
    consentAccepting:     'Joining...',
    consentExpired:       'This invitation has expired. Ask your coach to send a new one.',
    consentInvalid:       'Invalid invitation.',
    consentAlreadyMember: "You're already a member of this team.",
    consentAcceptError:   'Failed to join the team.',
    consentSupportHint:   'If the problem persists, contact support:',

    syncNotice:    'Stats synced with your team.',
    syncRateLimit: 'Sync skipped (wait 60 seconds between syncs).',

    pricingTitle:        'GrindLog for Teams',
    pricingSubtitle:     'Pro for every player. Coach dashboard. Privacy first.',
    pricingMonthly:      'Monthly',
    pricingAnnual:       'Annual · save 20%',
    pricingPerMonth:     'mo',
    pricingPerYear:      'yr',
    pricingCheckoutBtn:  'Subscribe ·',
    pricingContact:      'More than 20 players? Contact us:',
    pricingFeature1:     'Pro for every team player',
    pricingFeature2:     'Coach dashboard with team-wide leaks',
    pricingFeature3:     'Per-player drill-down (with consent)',
    pricingFeature4:     'Hand histories never leave your device',
    pricingLaunchBadge:  'First 10 teams: price locked for 2 years',

    seatsPlanActive:    'You have {{seats}} seats activated. You can now create your team.',
    seatsActiveInvited: '{{active}} active · {{invited}} invited',
    seatsAtCapacity:    'All seats are taken. To expand your plan contact:',
    seatsOverAllocated: 'Your team has {{actual}} seats used but you contracted {{contracted}}. You can\'t invite more until this is resolved. Contact:',

    consentSeatsFull: 'This team is full. No seats available. Contact your coach to expand the plan.',

    // Pending invites
    pendingInvitesTitle:  'Pending invitations',
    inviteExpires:        'Expires',
    inviteRevoke:         'Cancel',

    // Transfer ownership
    transferOwnershipTitle:      'Transfer ownership',
    transferOwnershipDesc:       'Hand over control of the team to another active member. This cannot be undone — you become a regular player.',
    transferOwnershipSelect:     'Choose new coach...',
    transferOwnershipConfirm:    'Transfer control to {{email}}?',
    transferOwnershipConfirmBtn: 'Confirm transfer',

    // A1: Per-player full view (CoachPlayerView)
    viewPlayerStats:        'View stats',
    playerStatsSubtitle:    'Player stats',
    playerStatsHands:       'hands',
    playerStatsClose:       'Close',
    playerStatsNoData:      'This player has not synced any analysis yet.',
    playerStatsLeakCritical:'critical leak(s)',
    playerStatsLeakWarn:    'warning(s)',

    // A2: Team comparison table (TeamComparisonTable)
    comparisonTitle:  'Team comparison',
    comparisonNoData: 'No members have synced stats yet.',
    colCbet:          'CBet',
    colFoldTo3bet:    'Fold/3B',
    colFoldToPush:    'Fold/Push',
    legendOk:         'within range',
    legendWarn:       'out of range',
    legendCritical:   'critical leak',

    // Coaching modal tabs
    statsTabPreflop:  'Preflop',
    statsTabPostflop: 'Postflop',

    // A3: Team leaks panel (TeamLeaksPanel)
    leaksTitle:            'Team leaks',
    leaksNoData:           'No members have synced stats yet.',
    leaksSummaryPlayers:   'player(s) with critical leaks',
    leaksSummaryWarnOnly:  'warnings only',
    leaksAllGood:          'No critical leaks detected',
    leaksFilterCritical:   '● Critical',
    leaksFilterAll:        'All',
    leaksBadgeCritical:    'critical',
    leaksBadgeWarn:        'warning(s)',
    leaksPlayerClean:      'No leaks detected',
    leaksLowSample:        'low sample',
    leaksRange:            'range',

    // Benchmark editor
    benchmarks: {
      sectionTitle:    'Team Benchmarks',
      title:           'BENCHMARK OVERRIDES',
      subtitle:        'Set custom target ranges for your players. Leave a field empty to use the global default.',
      loading:         'Loading benchmarks...',
      colStat:         'Stat',
      colPosition:     'Position',
      colDefault:      'Default',
      colOverrideMin:  'Min',
      colOverrideMax:  'Max',
      allFallbackHint: '(ALL applies to all stacks unless overridden)',
      saveBtn:         'Save benchmarks',
      saving:          'Saving...',
      savedOk:         '✓ Saved',
      savedErr:        'Error saving. Try again.',
      resetCategory:   'Reset category',
      footerHint:      'Players in this team will see these ranges in their stats dashboard.',
    },
  },
};
